/* eslint-disable  @typescript-eslint/no-explicit-any */

import { useState, useCallback, useMemo } from 'react';
import i18next from 'utils/i18n';
type ValidationRuleType<T> = {
  type: string;
  message: string;
  validate: (value: T, allValues?: Record<string, any>) => boolean;
};
export const isEmpty = (value: any): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'boolean' && value == false) ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

export const isAdultValidator = (dob: string): boolean => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  // Adjust if the birthday hasn't occurred yet this year
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age >= 18;
};
class ValidationRule<T> {
  fieldName: string;
  rules: Array<ValidationRuleType<T>>;

  constructor(fieldName: string) {
    this.fieldName = fieldName;
    this.rules = [];
  }

  required(message = i18next.t('defaultValidations.required')): this {
    this.rules.push({
      type: 'required',
      message,
      validate: (value: T) => !isEmpty(value),
    });
    return this;
  }

  email(message = i18next.t('defaultValidations.invalidEmail')): this {
    this.rules.push({
      type: 'email',
      message,
      validate: (value: T) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value)),
    });
    return this;
  }

  min(length: number, message = `Minimum length is ${length}`): this {
    this.rules.push({
      type: 'min',
      message,
      validate: (value: T) => !value || String(value).length >= length,
    });
    return this;
  }

  max(length: number, message = `Maximum length is ${length}`): this {
    this.rules.push({
      type: 'max',
      message,
      validate: (value: T) => !value || String(value).length <= length,
    });
    return this;
  }
  minNumber(minValue: number, message = `Value must be at least ${minValue}`): this {
    this.rules.push({
      type: 'minNumber',
      message,
      validate: (value: T) => typeof value === 'number' && value >= minValue,
    });
    return this;
  }

  maxNumber(maxValue: number, message = `Value must be at most ${maxValue}`): this {
    this.rules.push({
      type: 'maxNumber',
      message,
      validate: (value: T) => typeof value === 'number' && value <= maxValue,
    });
    return this;
  }
  isNumber(message = 'Must be a number'): this {
    this.rules.push({
      type: 'isNumber',
      message,
      validate: (value: T) => typeof value === 'number',
    });
    return this;
  }

  isBoolean(message = 'Must be a boolean'): this {
    this.rules.push({
      type: 'isBoolean',
      message,
      validate: (value: T) => typeof value === 'boolean',
    });
    return this;
  }
  matches(pattern: RegExp, message = 'Invalid format'): this {
    this.rules.push({
      type: 'matches',
      message,
      validate: (value: T) => !value || pattern.test(String(value)),
    });
    return this;
  }
  isAdult(message = i18next.t('defaultValidations.ageMustBeGreaterThan18')): this {
    this.rules.push({
      type: 'isAdult',
      message,
      validate: (dob: T) => isAdultValidator(dob as string),
    });
    return this;
  }
  custom(
    validator: (value: T, allValues?: Record<string, any>) => boolean,
    message = i18next.t('defaultValidations.invalidField')
  ): this {
    this.rules.push({
      type: 'custom',
      message,
      validate: validator,
    });
    return this;
  }
}

const validate = {
  string: (fieldName: string) => new ValidationRule<string>(fieldName),
  number: (fieldName: string) => new ValidationRule<number>(fieldName),
  boolean: (fieldName: string) => new ValidationRule<boolean>(fieldName),
};

type ValidationSchema<T> = {
  [K in keyof T]: ValidationRule<T[K]>;
};

type FormErrors<T> = {
  [K in keyof T]?: string;
};

type TouchedFields<T> = {
  [K in keyof T]?: boolean;
};

interface UseFormProps<T> {
  initialValues: T;
  validationSchema: ValidationSchema<T>;
  onSubmit: (values: T) => Promise<void> | void;
  onValidationError?: (errors: FormErrors<T>, values: T) => void;
}

interface UseFormReturn<T> {
  values: T;
  errors: FormErrors<T>;
  touched: TouchedFields<T>;
  isSubmitting: boolean;
  handleChange: (field: keyof T, value: T[keyof T]) => void;
  handleBlur: (field: keyof T) => void;
  setValues: (values: T) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  resetForm: () => void;
}

const createValidator = <T extends Record<string, any>>(schema: ValidationSchema<T>) => {
  return (values: T): FormErrors<T> => {
    const errors: FormErrors<T> = {};

    for (const field in schema) {
      const value = values[field];
      const fieldValidation = schema[field];

      for (const rule of fieldValidation.rules) {
        if (!rule.validate(value, values)) {
          errors[field] = rule.message;
          break;
        }
      }
    }

    return errors;
  };
};

const useForm = <T extends Record<string, any>>({
  initialValues,
  validationSchema,
  onSubmit,
  onValidationError,
}: UseFormProps<T>): UseFormReturn<T> => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<FormErrors<T>>({});
  const [touched, setTouched] = useState<TouchedFields<T>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = useMemo(() => createValidator<T>(validationSchema), [validationSchema]);

  const handleChange = useCallback((field: keyof T, value: T[keyof T]) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  }, []);

  const handleBlur = useCallback(
    (field: keyof T) => {
      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }));

      const fieldErrors = validate({ ...values });
      setErrors((prev) => ({
        ...prev,
        [field]: fieldErrors[field],
      }));
    },
    [validate, values]
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault();

      const formErrors = validate(values);
      setErrors(formErrors);

      const touchedFields = Object.keys(values).reduce<TouchedFields<T>>((acc, key) => ({ ...acc, [key]: true }), {});
      setTouched(touchedFields);

      if (Object.keys(formErrors).length === 0) {
        setIsSubmitting(true);
        try {
          await onSubmit(values);
        } catch (error) {
          console.error('Form submission error:', error);
        }
        setIsSubmitting(false);
      } else {
        onValidationError?.(formErrors, values);
      }
    },
    [validate, values, onSubmit, onValidationError]
  );

  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
    resetForm,
  };
};

export default useForm;
export { validate };